/**
 * ステップ見積もり -超松竹梅- のプリセットペイロード（直列化・検証）。
 *
 * ツールプリセット基盤（`useToolPresetBrowser` + `/api/me/tools/.../presets`）に
 * 保存する JSON の組み立てと、読み込み時のサニタイズを担う。
 * payload は BE で 32KB 制限があるため、サムネイルは S3 の URL 参照のみを持つ
 * （base64 等のインライン画像は保存しない）。
 *
 * サニタイズ方針は phrase-bloom と同じ「読めるところは読む」:
 * ペイロード全体がオブジェクトでない場合のみ null、行単位では欠損フィールドを
 * デフォルト値で補完する。
 */

import type { EstimateItemRow } from "./estimateModel";

export const STEP_ESTIMATE_TOOL_ID = "step-estimate";

/**
 * ペイロード形式のバージョン。互換の無い構造変更をしたら上げる。
 * v2: 宛名（customerName）・件名（subject）を表と一緒に保存するようになった。
 * v3: 発行者情報（issuer: 会社名・肩書き・住所）も一緒に保存するようになった。
 *     v1 / v2 のペイロードは欠けているフィールドを空文字として読み込む。
 */
export const STEP_ESTIMATE_SCHEMA_VERSION = 3;

/**
 * 御見積書の発行者欄に載せる情報。
 *
 * 初期値はマイページの登録内容（所属・タイトル・請求先住所）から入れるが、
 * 案件ごとに書き換えられるよう表と一緒にプリセットへ保存する。
 * 住所は 2 行に分けられる（1 行目に〒＋市区町村、2 行目に建物名など）。
 */
export interface EstimateIssuer {
  companyName: string;
  jobTitle: string;
  addressLine1: string;
  addressLine2: string;
}

export const createEmptyIssuer = (): EstimateIssuer => ({
  companyName: "",
  jobTitle: "",
  addressLine1: "",
  addressLine2: "",
});

export interface StepEstimatePresetPayload {
  rows: EstimateItemRow[];
  /** 御見積書の件名（任意）。 */
  subject: string;
  /** 御見積書の宛名（任意・「御中」は PDF テンプレート側で付く）。 */
  customerName: string;
  /** 御見積書の発行者欄（任意）。 */
  issuer: EstimateIssuer;
}

// ===== 行のデフォルト値 =====

export const createEmptyRow = (
  partial: Partial<EstimateItemRow> = {},
): EstimateItemRow => ({
  id: crypto.randomUUID(),
  name: "",
  unitPrice: "",
  quantity: 1,
  unit: "式",
  note: "",
  quantityVariable: false,
  quantityMin: 1,
  quantityMax: 10,
  quantityDefault: 1,
  itemDescription: "",
  itemThumbnailUrl: "",
  optionText: "",
  optionThumbnailUrl: "",
  ...partial,
});

// ===== 直列化 =====

/** 現在の表＋宛名・件名・発行者を直列化する（キー順を安定させる）。 */
export const buildPresetPayload = (
  rows: EstimateItemRow[],
  subject = "",
  customerName = "",
  issuer: EstimateIssuer = createEmptyIssuer(),
): StepEstimatePresetPayload => ({
  rows: rows.map((row) => ({
    id: row.id,
    name: row.name,
    unitPrice: row.unitPrice,
    quantity: row.quantity,
    unit: row.unit,
    note: row.note,
    quantityVariable: row.quantityVariable,
    quantityMin: row.quantityMin,
    quantityMax: row.quantityMax,
    quantityDefault: row.quantityDefault,
    itemDescription: row.itemDescription,
    itemThumbnailUrl: row.itemThumbnailUrl,
    optionText: row.optionText,
    optionThumbnailUrl: row.optionThumbnailUrl,
  })),
  subject,
  customerName,
  issuer: {
    companyName: issuer.companyName,
    jobTitle: issuer.jobTitle,
    addressLine1: issuer.addressLine1,
    addressLine2: issuer.addressLine2,
  },
});

// ===== サニタイズ =====

const asString = (value: unknown, fallback = ""): string =>
  typeof value === "string" ? value : fallback;

const asBoolean = (value: unknown, fallback: boolean): boolean =>
  typeof value === "boolean" ? value : fallback;

const asNumber = (value: unknown, fallback: number): number =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

const sanitizeRow = (raw: unknown): EstimateItemRow => {
  const r =
    typeof raw === "object" && raw !== null && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {};
  const d = createEmptyRow();

  const quantityMin = asNumber(r.quantityMin, d.quantityMin);
  const quantityMax = Math.max(quantityMin, asNumber(r.quantityMax, d.quantityMax));
  const quantityDefault = Math.min(
    quantityMax,
    Math.max(quantityMin, asNumber(r.quantityDefault, quantityMin)),
  );

  return {
    id: asString(r.id) || d.id,
    name: asString(r.name),
    // 旧データが数値で保存されていても文字列へ寄せる
    unitPrice:
      typeof r.unitPrice === "number" ? String(r.unitPrice) : asString(r.unitPrice),
    quantity: Math.max(0, asNumber(r.quantity, d.quantity)),
    unit: asString(r.unit, d.unit),
    note: asString(r.note),
    quantityVariable: asBoolean(r.quantityVariable, false),
    quantityMin,
    quantityMax,
    quantityDefault,
    itemDescription: asString(r.itemDescription),
    itemThumbnailUrl: asString(r.itemThumbnailUrl),
    optionText: asString(r.optionText),
    optionThumbnailUrl: asString(r.optionThumbnailUrl),
  };
};

const sanitizeIssuer = (raw: unknown): EstimateIssuer => {
  const r =
    typeof raw === "object" && raw !== null && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {};
  return {
    companyName: asString(r.companyName),
    jobTitle: asString(r.jobTitle),
    addressLine1: asString(r.addressLine1),
    addressLine2: asString(r.addressLine2),
  };
};

/**
 * 保存済みペイロードを検証し、安全な `StepEstimatePresetPayload` に正規化する。
 * ペイロード全体がオブジェクトでない・rows が配列でない場合のみ null。
 * 古いバージョンで欠けているフィールド（subject / customerName / issuer）は
 * 空文字で読み込む。
 */
export const sanitizePresetPayload = (
  raw: unknown,
): StepEstimatePresetPayload | null => {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return null;
  }
  const obj = raw as Record<string, unknown>;
  const rows = obj.rows;
  if (!Array.isArray(rows)) return null;

  const sanitized = rows.map(sanitizeRow);
  // 行 id の重複（手書き JSON 等）は後勝ちで振り直して選択状態の混線を防ぐ
  const seen = new Set<string>();
  for (const row of sanitized) {
    if (seen.has(row.id)) row.id = crypto.randomUUID();
    seen.add(row.id);
  }
  return {
    rows: sanitized,
    subject: asString(obj.subject),
    customerName: asString(obj.customerName),
    issuer: sanitizeIssuer(obj.issuer),
  };
};
